/**
 * useActiveGames Hook
 * Fetch and subscribe to currently active game sessions
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { GameSession, GameDefinition, ActiveGame } from "../types";

interface UseActiveGamesReturn {
  /** Active games (currently being played) */
  activeGames: ActiveGame[];
  /** Games waiting for players */
  waitingGames: ActiveGame[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch active games */
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and subscribe to active game sessions
 */
export const useActiveGames = (): UseActiveGamesReturn => {
  const { activeWeekend } = useAuth();

  const [activeGames, setActiveGames] = useState<ActiveGame[]>([]);
  const [waitingGames, setWaitingGames] = useState<ActiveGame[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const subscriptionRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  /**
   * Transform session data into ActiveGame format
   */
  const transformToActiveGame = (session: GameSession & { game: GameDefinition }): ActiveGame => {
    const startedAt = session.started_at ? new Date(session.started_at) : new Date(session.created_at);
    const secondsAgo = Math.floor((Date.now() - startedAt.getTime()) / 1000);

    return {
      session,
      game: session.game!,
      participants: session.participants.map((p) => ({
        user_id: p.user_id,
        full_name: p.display_name,
        avatar_url: p.avatar_url || null,
      })),
      started_ago_seconds: secondsAgo,
      is_waiting: session.status === "waiting",
    };
  };

  /**
   * Fetch active and waiting games
   */
  const fetchGames = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch active and waiting sessions
      const { data, error: fetchError } = await supabase
        .from("game_sessions")
        .select(`
          *,
          game:game_definitions!game_sessions_game_slug_fkey(*)
        `)
        .in("status", ["active", "waiting"])
        .order("started_at", { ascending: false, nullsFirst: false })
        .limit(50);

      if (fetchError) throw fetchError;

      const sessions = (data || []) as (GameSession & { game: GameDefinition })[];

      // Split into active and waiting
      const active: ActiveGame[] = [];
      const waiting: ActiveGame[] = [];

      for (const session of sessions) {
        const activeGame = transformToActiveGame(session);
        if (session.status === "waiting") {
          waiting.push(activeGame);
        } else {
          active.push(activeGame);
        }
      }

      setActiveGames(active);
      setWaitingGames(waiting);
    } catch (err) {
      console.error("Error fetching active games:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch active games"));
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Subscribe to game session changes
   */
  const subscribeToChanges = useCallback(() => {
    // Unsubscribe from previous subscription
    if (subscriptionRef.current) {
      supabase.removeChannel(subscriptionRef.current);
    }

    subscriptionRef.current = supabase
      .channel("active_games")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "game_sessions",
        },
        (payload) => {
          // Refetch on any change to game_sessions
          if (
            payload.eventType === "INSERT" ||
            payload.eventType === "UPDATE" ||
            payload.eventType === "DELETE"
          ) {
            fetchGames();
          }
        }
      )
      .subscribe();
  }, [fetchGames]);

  // Initial fetch and subscription
  useEffect(() => {
    fetchGames();
    subscribeToChanges();

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, [fetchGames, subscribeToChanges]);

  return {
    activeGames,
    waitingGames,
    isLoading,
    error,
    refetch: fetchGames,
  };
};

/**
 * Get active games for a specific game type
 */
export const useActiveGamesBySlug = (gameSlug: string) => {
  const { activeGames, waitingGames, isLoading, error, refetch } = useActiveGames();

  return {
    activeGames: activeGames.filter((g) => g.session.game_slug === gameSlug),
    waitingGames: waitingGames.filter((g) => g.session.game_slug === gameSlug),
    isLoading,
    error,
    refetch,
  };
};

